import React, { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { X, Settings, Shield, ChevronDown, ChevronUp } from "lucide-react";
import { API } from "../App";
import axios from "axios";

// Cookie consent storage key
const CONSENT_KEY = "smartbooker_cookie_consent";
const CONSENT_SESSION_KEY = "consent_session_id";

// Generate session ID for tracking consent
const generateSessionId = () => `consent_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, can't be disabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Hide banner on the auto-playing video demo page (it would block the
    // recording / look terrible when the prospect is watching).
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/video-demo")) {
      return;
    }
    // Hide banner on the public patient sign route — it is a one-shot
    // transactional flow, no tracking cookies are set, and the modal blocks
    // the wizard on mobile devices.
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/sign/")) {
      return;
    }
    // Hide banner on the patient-facing wellness routes (portal + exercise
    // routine view). These are framed as the studio's own app — surfacing
    // SmartBooker branding/cookies would break the white-label feel.
    if (typeof window !== "undefined" && (
      window.location.pathname.startsWith("/portal/") ||
      window.location.pathname.startsWith("/exercises/")
    )) {
      return;
    }
    // Check if consent was already given
    const storedConsent = localStorage.getItem(CONSENT_KEY);
    if (!storedConsent) {
      // No consent stored, show banner after a small delay
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Load stored preferences
      try {
        const parsed = JSON.parse(storedConsent);
        setPreferences(parsed.preferences || preferences);
        // Apply stored preferences
        applyPreferences(parsed.preferences);
      } catch (e) {
        // Invalid stored data, show banner
        setShowBanner(true);
      }
    }
  }, []);

  const applyPreferences = (prefs) => {
    // This function controls which cookies/scripts are enabled
    // Essential cookies are always enabled
    
    if (prefs.analytics) {
      // Enable analytics (e.g., Google Analytics)
      window.analyticsEnabled = true;
    } else {
      window.analyticsEnabled = false;
      // Remove analytics cookies if they exist
      document.cookie.split(";").forEach(cookie => {
        const name = cookie.split("=")[0].trim();
        if (name.startsWith("_ga") || name.startsWith("_gid")) {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });
    }

    if (prefs.marketing) {
      // Enable marketing cookies
      window.marketingEnabled = true;
    } else {
      window.marketingEnabled = false;
    }

    if (prefs.functional) {
      // Enable functional cookies (e.g., preferences, language)
      window.functionalEnabled = true;
    } else {
      window.functionalEnabled = false;
    }
  };

  const saveConsent = async (prefs) => {
    const sessionId = localStorage.getItem(CONSENT_SESSION_KEY) || generateSessionId();
    localStorage.setItem(CONSENT_SESSION_KEY, sessionId);
    
    // Store locally
    const consentData = {
      preferences: prefs,
      consentDate: new Date().toISOString(),
      version: "v1.0_2026-03-13"
    };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    
    // Store in backend for compliance
    try {
      await axios.post(`${API}/cookie-consent`, prefs, {
        withCredentials: true,
        headers: {
          "X-Consent-Session": sessionId
        }
      });
    } catch (error) {
      console.error("Failed to save cookie consent to backend:", error);
    }
    
    // Apply preferences
    applyPreferences(prefs);
    
    // Hide banner
    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyEssential);
    saveConsent(onlyEssential);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === "essential") return; // Can't disable essential
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Public method to re-open preferences
  useEffect(() => {
    window.openCookiePreferences = () => {
      setShowDetails(true);
      setShowBanner(true);
    };
  }, []);

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={() => {}} // Prevent closing without choice
      />
      
      {/* Banner */}
      <div 
        className="relative bg-background border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg mx-4 mb-0 sm:mb-0 max-h-[90vh] overflow-hidden animate-in slide-in-from-bottom duration-300"
        data-testid="cookie-banner"
      >
        {/* Header */}
        <div className="p-6 pb-4 border-b border-border">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-accent" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">Gestione Cookie</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Utilizziamo i cookie per migliorare la tua esperienza. Puoi personalizzare le tue preferenze.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
          {!showDetails ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Questo sito utilizza cookie tecnici necessari per il funzionamento e, con il tuo consenso, 
                cookie di analisi e profilazione per offrirti una migliore esperienza.
              </p>
              <button
                onClick={() => setShowDetails(true)}
                className="flex items-center gap-2 text-sm text-accent hover:underline"
                data-testid="cookie-customize-btn"
              >
                <Settings className="w-4 h-4" />
                Personalizza preferenze
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <button
                onClick={() => setShowDetails(false)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ChevronUp className="w-4 h-4" />
                Nascondi dettagli
              </button>

              {/* Cookie Categories */}
              <div className="space-y-3">
                {/* Essential */}
                <div className="p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sm">Cookie Essenziali</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Necessari per il funzionamento del sito. Non possono essere disattivati.
                      </p>
                    </div>
                    <div className="w-10 h-6 bg-accent/20 rounded-full flex items-center justify-end px-1">
                      <div className="w-4 h-4 bg-accent rounded-full" />
                    </div>
                  </div>
                </div>

                {/* Analytics */}
                <div 
                  className="p-4 rounded-lg border cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => togglePreference("analytics")}
                  data-testid="cookie-analytics-toggle"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sm">Cookie Analitici</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Ci aiutano a capire come utilizzi il sito per migliorarlo.
                      </p>
                    </div>
                    <div 
                      className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.analytics ? 'bg-accent/20 justify-end' : 'bg-muted justify-start'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full transition-colors ${
                        preferences.analytics ? 'bg-accent' : 'bg-muted-foreground/50'
                      }`} />
                    </div>
                  </div>
                </div>

                {/* Marketing */}
                <div 
                  className="p-4 rounded-lg border cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => togglePreference("marketing")}
                  data-testid="cookie-marketing-toggle"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sm">Cookie di Marketing</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Utilizzati per mostrarti contenuti e offerte pertinenti.
                      </p>
                    </div>
                    <div 
                      className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.marketing ? 'bg-accent/20 justify-end' : 'bg-muted justify-start'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full transition-colors ${
                        preferences.marketing ? 'bg-accent' : 'bg-muted-foreground/50'
                      }`} />
                    </div>
                  </div>
                </div>

                {/* Functional */}
                <div 
                  className="p-4 rounded-lg border cursor-pointer hover:bg-muted/20 transition-colors"
                  onClick={() => togglePreference("functional")}
                  data-testid="cookie-functional-toggle"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium text-sm">Cookie Funzionali</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Memorizzano le tue preferenze per una migliore esperienza.
                      </p>
                    </div>
                    <div 
                      className={`w-10 h-6 rounded-full flex items-center px-1 transition-colors ${
                        preferences.functional ? 'bg-accent/20 justify-end' : 'bg-muted justify-start'
                      }`}
                    >
                      <div className={`w-4 h-4 rounded-full transition-colors ${
                        preferences.functional ? 'bg-accent' : 'bg-muted-foreground/50'
                      }`} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Links to legal pages */}
          <div className="pt-2 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Per maggiori informazioni consulta la nostra{" "}
              <a href="/privacy-policy" className="text-accent hover:underline" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              {" "}e la{" "}
              <a href="/cookie-policy" className="text-accent hover:underline" target="_blank" rel="noopener noreferrer">
                Cookie Policy
              </a>.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 pt-4 border-t border-border bg-muted/20">
          <div className="flex flex-col sm:flex-row gap-3">
            {showDetails ? (
              <>
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={handleRejectAll}
                  data-testid="cookie-reject-btn"
                >
                  Rifiuta tutti
                </Button>
                <Button
                  className="flex-1 rounded-full"
                  onClick={handleSavePreferences}
                  data-testid="cookie-save-preferences-btn"
                >
                  Salva preferenze
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  className="flex-1 rounded-full"
                  onClick={handleRejectAll}
                  data-testid="cookie-reject-btn"
                >
                  Rifiuta
                </Button>
                <Button
                  className="flex-1 rounded-full bg-accent hover:bg-accent/90"
                  onClick={handleAcceptAll}
                  data-testid="cookie-accept-btn"
                >
                  Accetta tutti
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Export function to check if a specific cookie type is allowed
export const isCookieAllowed = (type) => {
  const storedConsent = localStorage.getItem(CONSENT_KEY);
  if (!storedConsent) return type === "essential";
  
  try {
    const parsed = JSON.parse(storedConsent);
    return parsed.preferences?.[type] ?? (type === "essential");
  } catch (e) {
    return type === "essential";
  }
};

// Export function to open cookie preferences
export const openCookiePreferences = () => {
  if (window.openCookiePreferences) {
    window.openCookiePreferences();
  }
};

export default CookieBanner;
